/**
 * @fileoverview Input/output schemas for plot-thread-extractor-v1.
 *
 * This agent analyzes full source text to extract plot threads —
 * lightweight narrative segments with driving questions.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 * Consumer hooks transform output into domain PlotThread entities.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Uses existing prompt variables:
 * - textInput: The full source text to analyze for plot threads
 * - storyContext: Story metadata for context
 * - chapterDirectory: Lightweight chapter list for assigning threads to chapters
 * - storyType: Story type for calibrating expected thread count
 * - projectedWordCount: Target word count for calibrating expected thread count
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.textInput.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterDirectory.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyType.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.projectedWordCount.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * A single extracted plot thread.
 */
const PlotThreadOutputSchema = z.object({
  title: z.string().describe(
    'Short, evocative title for the thread (2-5 words). ' +
    'Examples: "The Police Chase", "Sibling Rivalry", "The Locked Room".'
  ),
  summary: z.string().describe(
    'What this thread is about — the context that makes the question meaningful (1-3 sentences). ' +
    'Example: "Captain Fache pursues Langdon through Paris, convinced he murdered the curator."'
  ),
  threadQuestion: z.string().describe(
    'The dramatic question driving this thread. Must be phrased as a question. ' +
    'Example: "Will they catch him before he proves his innocence?"'
  ),
  characterNames: z.array(z.string()).describe(
    'Names of characters directly involved in this thread.'
  ),
  status: z.enum(['active', 'resolved']).describe(
    'Whether this thread is still open ("active") or concluded in the text ("resolved").'
  ),
  introducedInChapterNumber: z.number().nullable().describe(
    'The chapter number where this thread is first introduced. Null if unclear.'
  ),
  resolvedInChapterNumber: z.number().nullable().describe(
    'The chapter number where this thread is resolved. Null if still active or unclear.'
  ),
  activeInChapterNumbers: z.array(z.number()).describe(
    'Chapter numbers where this thread actively advances or is meaningfully present.'
  ),
});

/**
 * Output schema for the plot-thread-extractor-v1 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility:
 * - Level 1: Root object
 * - Level 2: plotThreads array
 * - Level 3: Array items
 *
 * Consumer hooks transform into domain PlotThread entities.
 */
export const OutputSchema = z.object({
  plotThreads: z.array(PlotThreadOutputSchema).describe(
    'Extracted plot threads with titles, summaries, driving questions, and involved characters.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type PlotThreadOutput = z.infer<typeof PlotThreadOutputSchema>;
